/**
 * @file    resolveFeatureFlag.ts
 * @scope   SHARED
 * @summary Engine feature-flag resolver: localStorage override → dashboard config → default.
 * @consumes resolveFirst (layered resolver), injected dashboard config
 * @produces FeatureFlagSource, readLocalStorageFlag, resolveFeatureFlag, createFeatureFlagResolver
 * @sideEffects Reads `window.localStorage` (guarded).
 * @notes   Reuses Peacock's existing conventions instead of inventing new ones (§12):
 *          - the flag name IS the `configDashboard` key injected via `public/index.html`
 *            (like `dataGridFeatureFlag`, `useConversationAnalysisV2`);
 *          - the same name is the localStorage key, with 'true'/'false' string values, exactly as
 *            `components/ConversationAnalysis/shared/featureFlag.ts`.
 *          Toggle from the browser: localStorage.setItem('<flag>', 'true' | 'false').
 */
import { resolveFirst, ResolverLayer } from '../resolution/layeredResolver';

/** Dashboard config as injected in the store (`configDashboard`). */
export type FeatureFlagSource = Record<string, unknown> | undefined;

/**
 * @param flag localStorage key.
 * @returns `true`/`false` when overridden, `undefined` when not set or storage is unavailable.
 */
export const readLocalStorageFlag = (flag: string): boolean | undefined => {
  try {
    const stored = window.localStorage.getItem(flag);
    if (stored !== null) return stored === 'true';
  } catch {
    // WHY: localStorage may be unavailable (e.g. private mode) — fall through to config.
  }
  return undefined;
};

const readConfigFlag =
  (config: FeatureFlagSource): ResolverLayer<string, boolean> =>
  (flag) => {
    const value = config?.[flag];
    return typeof value === 'boolean' ? value : undefined;
  };

/**
 * @param flag Flag name (config key + localStorage key).
 * @param config Dashboard config.
 * @param defaultValue Used when neither layer defines the flag. Off by default (ship dark).
 */
export const resolveFeatureFlag = (
  flag: string,
  config: FeatureFlagSource,
  defaultValue = false
): boolean => resolveFirst([readLocalStorageFlag, readConfigFlag(config)], flag, defaultValue);

/**
 * @param config Dashboard config.
 * @returns `(flag?) => boolean`; a missing flag means "not gated" → enabled.
 */
export const createFeatureFlagResolver =
  (config: FeatureFlagSource) =>
  (flag?: string): boolean =>
    !flag || resolveFeatureFlag(flag, config);
